import React, { useCallback, useContext, useMemo, useRef } from "react";
import { debounce, flatten } from "lodash";
import { z } from "zod";
import { formatCoordinates } from "common/utils/coordinates";
import { SavedView } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import useURLParsing from "../../../hooks/useURLParsing";
import { LayerContext, LayerContextType } from "../layers";
import { getInteractiveLayerById } from "../layers/customMap";
import SavedViewsLayer, {
  viewsLayerId,
  viewsSourceId,
} from "../layers/savedViews";
import { CustomMapLayerValue, GeometryRef } from "./customMapLayer";
import {
  findRelevantFeature,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
  Property,
} from "./types";
import {
  handleOnClickForCustomMap,
  handleOnHoverForCustomMap,
  makePropertyFromFeature,
  useFeatureClickState,
} from "./utils";

export type SavedViewsLayerConfig = LayerConfig<
  Array<Pick<SavedView, "id" | "query">>,
  Property | CustomMapLayerValue
> & { savedViews: Array<{ id: string }> };

export const getInteractiveLayerIds = (
  savedViews: SavedViewsLayerConfig["value"],
  isLayerVisible: LayerContextType["isLayerVisible"]
) => {
  return flatten(
    (savedViews ?? [])
      .filter(({ id }) => isLayerVisible({ group: "savedViews", id }))
      .map(({ id }) => {
        const base = `${viewsLayerId}-${id}` as LayerId;
        const interactive = getInteractiveLayerById(base);
        return [base, ...interactive];
      })
  );
};

const useSavedViewsHook: LayerHookBuilder<SavedViewsLayerConfig> = ({
  config,
  helpers,
  mapLoaded,
}) => {
  const { addErrorToast } = useStatusToasts();
  const { isLayerVisible } = useContext(LayerContext);
  const addErrorToastCallback = useCallback(() => {
    addErrorToast(
      `Unable to load table view data. Please try again. If the issue continues, contact support@withforerunner.com`
    );
  }, [addErrorToast]);

  const hoveredGeometry = useRef<Maybe<GeometryRef>>(null);
  const customObjParams = z.object({
    geometryId: z.string().optional(),
    customMapId: z.string().optional(),
  });
  const result = useURLParsing({
    routeName: "map",
    parser: customObjParams,
  });
  const geometryId = result?.geometryId;
  const customMapId = result?.customMapId;

  const interactiveLayerIds = getInteractiveLayerIds(
    config.value,
    isLayerVisible
  );

  const clickedGeometry = useFeatureClickState({
    value: {
      geometryId,
      customMapId,
    },
    allInteractiveLayerIds: interactiveLayerIds,
    helpers,
    mapLoaded,
  });

  return useMemo<LayerHook>(() => {
    return {
      interactiveLayerIds,
      // Need to be lower than properties
      zIndex: -1,
      canHandleHover: () => !!config.interactive?.hover,
      canHandleClick: () => !!config.interactive?.click,
      onHover: ({ event }) => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }

        const allLayerIds = getInteractiveLayerIds(
          config.value,
          isLayerVisible
        );
        const feature = findRelevantFeature(event, allLayerIds);

        if (feature) {
          helpers.setCursor("pointer");

          if (feature.properties.customMapId) {
            handleOnHoverForCustomMap({
              event,
              helpers,
              hoveredGeometry,
              feature,
            });
          } else {
            helpers.setCursor("pointer");

            helpers.emit({
              type: "setTooltip",
              data: {
                ...event.offsetCenter,
                text:
                  feature.properties.address ??
                  formatCoordinates({
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0],
                  }),
              },
            });
          }
        } else {
          const parcel = findRelevantFeature(event, "parcels");
          if (!parcel) {
            helpers.setCursor("grab");
          }
        }
      },
      onClick: ({ event }) => {
        const allLayerIds = getInteractiveLayerIds(
          config.value,
          isLayerVisible
        );
        const feature = findRelevantFeature(event, allLayerIds);

        if (feature) {
          if (feature.properties.customMapId) {
            handleOnClickForCustomMap({
              helpers,
              clickedGeometry,
              feature,
              config,
              fromSavedView: "true",
            });
          } else {
            config.onClick?.(makePropertyFromFeature(feature));
          }
          return true;
        } else {
          return false;
        }
      },
      render: () => {
        if (!config.savedViews.length) {
          return null;
        }

        return config.savedViews.map(savedView => (
          <SavedViewsLayer key={savedView.id} savedViewId={savedView.id} />
        ));
      },
      onFocusLost: () => {
        helpers.emit({ type: "removeTooltip", data: {} });
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }
      },

      shouldHandleLoadError: error =>
        (
          config.value?.map(({ id }) => `${viewsSourceId}-${id}`) ?? []
        ).includes(error.sourceId),
      onLoadError: () => {
        debounce(() => {
          addErrorToastCallback();
        }, 1000)();
      },
    };
  }, [
    config,
    addErrorToastCallback,
    isLayerVisible,
    helpers,
    interactiveLayerIds,
    clickedGeometry,
  ]);
};

export default useSavedViewsHook;
